import { useCallback, useState, useRef, useEffect } from 'react';
import { Avatar, Text } from '@client/toolkit';
import { useAppDispatch, useSession } from '../../../hooks';
import { logout, TenantSelectionEnum } from '../../../redux';
import { useTenants } from '../../../hooks/useTenants/useTenants';
import { useTranslation } from 'react-i18next';
import { FiLogOut, FiEdit2, FiX } from 'react-icons/fi';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { DashboardPaths } from '../../../pods/dashboard/dashboard.router';
import { useUserPermission } from '../../../hooks/useUserPermission';
import { TenantsDropdown } from '../../../components/tenants-selection-dropdown/tenants-dropdown';
import { LogoutConfirmModal } from '../../../components';
import { MePaths } from '../../../pods';

export function AppProfileMenu() {
  const { t } = useTranslation('type');
  const { t: tCommon } = useTranslation('common');
  const { sessionPerson, isFetching } = useSession();
  const { selectedTenant, allowedTenants, setSelectedTenant } = useTenants();
  const { role } = useUserPermission();
  const [isOpen, setIsOpen] = useState(false);
  const [tenantDropdownOpen, setTenantDropdownOpen] = useState(false);
  const [logoutModalOpen, setLogoutModalOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const handleToggleMenu = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setTenantDropdownOpen(false);
  }, []);

  const handleLogout = useCallback(() => {
    setLogoutModalOpen(true);
  }, []);

  const handleCancelLogout = useCallback(() => {
    setLogoutModalOpen(false);
  }, []);

  const handleConfirmLogout = useCallback(() => {
    dispatch(logout());
    setLogoutModalOpen(false);
    setIsOpen(false);
  }, [dispatch]);

  const handleEdit = useCallback(() => {
    setIsOpen(false);
    navigate('/profile');
  }, [navigate]);

  const handleTenantChange = async (tenantId: string) => {
    if (tenantId === TenantSelectionEnum.All) {
      setSelectedTenant(TenantSelectionEnum.All);
    } else {
      const tenant = allowedTenants.find((tenant) => tenant.id === tenantId);
      if (tenant) {
        setSelectedTenant(tenant);
      }
    }

    if (location.pathname === DashboardPaths.Dashboard || location.pathname === '/') {
      navigate(0);
    } else {
      navigate('/');
    }
    setIsOpen(false);
  };

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Don't close if tenant dropdown is open
      if (tenantDropdownOpen) {
        return;
      }

      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, tenantDropdownOpen]);

  // Close on window resize
  useEffect(() => {
    const handleResize = () => setIsOpen(false);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Reset tenant dropdown when parent menu closes
  useEffect(() => {
    if (!isOpen) {
      setTenantDropdownOpen(false);
    }
  }, [isOpen]);

  return (
    <div className="relative">
      <div
        ref={buttonRef}
        className="flex px-4 py-1 items-center cursor-pointer leading-none mr-4 hover:bg-menu-item-hover rounded-lg"
        onClick={handleToggleMenu}
      >
        <Avatar color="primary" className="mr-3" size="sm" loading={isFetching}>
          {sessionPerson?.initials}
        </Avatar>
        <div className="flex flex-col pr-3">
          <Text className="font-semibold" loading={isFetching} SkeletonProps={{ className: 'w-[100px]' }}>
            {sessionPerson?.name}
          </Text>
          <Text className="text-sm text-white/50" loading={isFetching} SkeletonProps={{ className: 'w-[80px]' }}>
            {role && role !== 'ClientDefault' ? t(`UserRole.${role}`) : ''}
          </Text>
        </div>
      </div>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          ref={menuRef}
          className="absolute right-4 top-full mt-2 w-[396px] bg-[#201E1A] rounded-b-lg shadow-xl z-50"
          style={{
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.5)',
          }}
        >
          {/* Header with Close Button */}
          <div className="flex items-center justify-between p-4 border-b border-[#322F2C]">
            <Text className="text-lg font-medium">Konto</Text>
            <button onClick={handleClose} className="p-1 hover:bg-[#272521] rounded transition-colors">
              <FiX size={20} />
            </button>
          </div>

          {/* Tenant Selection */}
          <div className="flex flex-col gap-2 p-4 border-b border-[#322F2C]" onClick={(e) => e.stopPropagation()}>
            <Text className="text-xs text-white/50 mb-2">KUNDEBASE</Text>
            <TenantsDropdown
              value={
                selectedTenant && selectedTenant !== TenantSelectionEnum.All
                  ? selectedTenant?.id
                  : TenantSelectionEnum.All
              }
              onChange={(e) => handleTenantChange(e.target.value as string)}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              open={tenantDropdownOpen}
              onOpen={() => setTenantDropdownOpen(true)}
              onClose={() => setTenantDropdownOpen(false)}
              variant="standard"
              showBorderAfter={false}
              className="w-full"
              items={allowedTenants}
            />
            {selectedTenant && selectedTenant !== TenantSelectionEnum.All ? (
              <button
                onClick={handleEdit}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-brand-primary hover:bg-[#d99540] text-black text-sm font-medium rounded-lg transition-colors whitespace-nowrap"
              >
                <FiEdit2 size={16} />
                {tCommon('profileAndSettings')}
              </button>
            ) : null}
          </div>

          {/* User Info */}
          <Link
            to={MePaths.Me}
            onClick={handleClose}
            className="flex items-center p-4 border-b border-[#322F2C] hover:bg-[#272521] transition-colors"
          >
            <Avatar color="primary" size="lg" loading={isFetching} className="mr-4">
              {sessionPerson?.initials}
            </Avatar>
            <div className="flex flex-col">
              <Text className="font-semibold text-lg" loading={isFetching}>
                {sessionPerson?.firstName} {sessionPerson?.lastName}
              </Text>
              <Text className="text-xs text-white/50" loading={isFetching}>
                {sessionPerson?.email}
              </Text>
              <span className="text-xs text-brand-primary font-semibold mt-1">{tCommon('myProfile')}</span>
            </div>
          </Link>

          {/* Action Buttons */}
          <div className="flex gap-3 p-4">
            <button
              onClick={handleLogout}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-button hover:bg-[#3a3732] text-white font-medium text-sm rounded-lg transition-colors whitespace-nowrap"
            >
              <FiLogOut size={15} />
              {tCommon('logout')}
            </button>
          </div>
        </div>
      )}

      <LogoutConfirmModal open={logoutModalOpen} onCancel={handleCancelLogout} onConfirm={handleConfirmLogout} />
    </div>
  );
}
